import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { ArrowLeft, Download } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';

import { purchaseOrderV2Api } from '~/api/purchaseOrderV2Api';
import { Alert } from '~/shadcn/components/ui/Alert';
import { Button } from '~/shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader } from '~/shadcn/components/ui/Card';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from '~/shared/components/bulkUpload';

const PO_COLUMN_DEFS = [
    // Top-level required fields
    { key: 'facility', label: 'Facility', required: true, conditionallyRequired: false },
    { key: 'vendor', label: 'Vendor', required: true, conditionallyRequired: false },
    // Shipper is required when deliveryType is "PICK UP"
    { key: 'shipper', label: 'Shipper', required: false, conditionallyRequired: true },
    // Cross Dock and Cross Dock Delivery Date are mutually required
    { key: 'crossDock', label: 'Cross Dock', required: false, conditionallyRequired: true },
    { key: 'crossDockDeliveryDate', label: 'Cross Dock Delivery Date', required: false, conditionallyRequired: true },
    { key: 'deliveryType', label: 'Delivery Type', required: true, conditionallyRequired: false },
    // Pick Up Date is required when deliveryType is "PICK UP"
    { key: 'pickUpDate', label: 'Pick Up Date', required: false, conditionallyRequired: true },
    {
        key: 'originalRequestedDeliveryDate',
        label: 'Original Requested Delivery Date',
        required: true,
        conditionallyRequired: false,
    },
    { key: 'scheduledDeliveryDate', label: 'Scheduled Delivery Date', required: true, conditionallyRequired: false },
    { key: 'minTemp', label: 'Min Temp', required: true, conditionallyRequired: false },
    { key: 'maxTemp', label: 'Max Temp', required: true, conditionallyRequired: false },
    { key: 'buyer1Email', label: 'Buyer 1 Email', required: true, conditionallyRequired: false },
    { key: 'buyer2Email', label: 'Buyer 2 Email', required: false, conditionallyRequired: false },
    { key: 'buyer3Email', label: 'Buyer 3 Email', required: false, conditionallyRequired: false },
    { key: 'buyer4Email', label: 'Buyer 4 Email', required: false, conditionallyRequired: false },
    { key: 'specialInstructions', label: 'Special Instructions', required: false, conditionallyRequired: false },
    { key: 'priority', label: 'Priority', required: false, conditionallyRequired: false },

    // Item-level required fields
    { key: 'intSku', label: 'Int SKU', required: true, conditionallyRequired: false },
    { key: 'vendorSku', label: 'Vendor SKU', required: false, conditionallyRequired: false },
    { key: 'caseQty', label: 'Case QTY', required: true, conditionallyRequired: false },
    { key: 'caseCost', label: 'Case Cost', required: true, conditionallyRequired: false },
    { key: 'csPallet', label: 'CS/Pallet', required: true, conditionallyRequired: false },
    { key: 'week', label: 'Week', required: true, conditionallyRequired: false },
    { key: 'year', label: 'Year', required: false, conditionallyRequired: false },
    { key: 'purchaseType', label: 'Purchase Type', required: true, conditionallyRequired: false },
    { key: 'srp', label: 'SRP', required: false, conditionallyRequired: false },
    // Expected dates are required when purchaseType is "OPPORTUNISTIC"
    { key: 'expectedProductionDate', label: 'Expected Production Date', required: false, conditionallyRequired: true },
    { key: 'expectedExpirationDate', label: 'Expected Expiration Date', required: false, conditionallyRequired: true },
    { key: 'whereWouldItGo', label: 'Where Would It Go?', required: false, conditionallyRequired: false },
    { key: 'whyImAMisfit', label: "Why I'm a Misfit", required: false, conditionallyRequired: false },
    { key: 'countryCodeOfOrigin', label: 'Country Code of Origin', required: true, conditionallyRequired: false },

    // Top-level optional fields
    { key: 'freightCost', label: 'Freight Cost', required: false, conditionallyRequired: false },
    // PO Number is required for add_item, override, and remove actions
    { key: 'poNumber', label: 'PO Number', required: false, conditionallyRequired: true },
    { key: 'action', label: 'Action', required: true, conditionallyRequired: false },
    // Reason is required for remove action
    { key: 'reason', label: 'Reason', required: false, conditionallyRequired: true },
];

function BulkCreatePurchaseOrder() {
    const history = useHistory();
    const location = useLocation();

    // Maintain filters from the purchase orders table for back navigation (same pattern as entity view)
    const purchaseOrdersFiltersFromState =
        typeof location.state?.purchaseOrdersFilters === 'string' && location.state.purchaseOrdersFilters.length > 0
            ? location.state.purchaseOrdersFilters
            : '';
    const purchaseOrdersFilters =
        purchaseOrdersFiltersFromState ||
        (typeof location?.search === 'string' && location.search ? location.search : '') ||
        (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
        '';
    const backHref = purchaseOrdersFilters
        ? { pathname: '/procurement/viewPurchaseOrder-v2', search: purchaseOrdersFilters }
        : '/procurement/viewPurchaseOrder-v2';

    const api = {
        edit: (key, params, rowsToUpdate, rowsToDelete) =>
            purchaseOrderV2Api.bulkEdit(key, params, rowsToUpdate, rowsToDelete),
        fetch: (key, params, signal) => purchaseOrderV2Api.bulkFetch(key, params, signal),
        persist: (key, params) => purchaseOrderV2Api.bulkPersist(key, params),
        upload: (file, params) => purchaseOrderV2Api.bulkUpload(file, params),
    };

    const bulkUpload = useBulkUpload({
        api,
        params: {},
    });

    const [persistSuccess, setPersistSuccess] = useState(false);

    // TODO: Move the getting of example data to the backend so there is one source of truth.
    const handleDownloadTemplate = useCallback(() => {
        const headers = PO_COLUMN_DEFS.map((col) => {
            if (col.required) {
                return `${col.label}*`;
            }
            if (col.conditionallyRequired) {
                return `${col.label}(*)`;
            }
            return col.label;
        }).join(',');

        // Generate example dates
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const twoWeeksLater = new Date();
        twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
        const sixMonthsLater = new Date();
        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
        const currentYear = new Date().getFullYear();
        const currentWeek = Math.ceil(
            ((new Date() - new Date(currentYear, 0, 1)) / 86400000 + new Date(currentYear, 0, 1).getDay() + 1) / 7,
        );

        // Build example row matching PO_COLUMN_DEFS order
        const exampleRow = {
            // Top-level required fields
            facility: 'LAX1',
            vendor: 'Example Vendor',
            deliveryType: 'PICK UP/DELIVERED',
            originalRequestedDeliveryDate: dayjs(nextWeek).format('MM/DD/YY'),
            scheduledDeliveryDate: dayjs(twoWeeksLater).format('MM/DD/YY'),
            buyer1Email: 'buyer1@example.com',

            // Top-level conditionally required fields
            shipper: 'Example Shipper',
            pickUpDate: dayjs(nextWeek).format('MM/DD/YY'),
            expectedProductionDate: dayjs(nextWeek).format('MM/DD/YY'),
            expectedExpirationDate: dayjs(sixMonthsLater).format('MM/DD/YY'),

            // Top-level optional fields
            crossDock: 'Example Cross Dock',
            crossDockDeliveryDate: '',
            minTemp: '32',
            maxTemp: '40',
            buyer2Email: '',
            buyer3Email: '',
            buyer4Email: '',
            specialInstructions: 'Handle with care',
            priority: 'true/false/yes/no',
            freightCost: '500.00',
            poNumber: '1234',
            action: 'add/add_item/override/remove',
            reason: '',

            // Item-level required fields
            intSku: '12345',
            caseQty: '100',
            caseCost: '25.00',
            csPallet: '40',
            week: String(currentWeek),
            purchaseType: 'EVERYDAY/OPPORTUNISTIC/PRIVATE_BRAND/SEASONAL',
            countryCodeOfOrigin: 'US',

            // Item-level optional fields
            vendorSku: 'VENDOR-SKU-001',
            year: String(currentYear),
            srp: '5.99',
            whereWouldItGo: 'Produce section',
            whyImAMisfit: 'Cosmetic imperfections|Other|Another reason',
        };

        const exampleValues = PO_COLUMN_DEFS.map((col) => {
            const value = exampleRow[col.key] ?? '';
            // Escape values that contain commas
            if (String(value).includes(',')) {
                return `"${value}"`;
            }
            return value;
        }).join(',');

        const csvContent = `${headers}\n${exampleValues}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'purchase_order_bulk_upload_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            // Only show success if there are no persist errors
            // (persist errors are row-level errors returned in a successful API response)
            const hasRowErrors = result?.persistResults?.errors?.length > 0;
            if (!hasRowErrors) {
                setPersistSuccess(true);
                bulkUpload.reset();
            }
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
    }, []);

    const handleFileSelect = useCallback(
        (file) => {
            if (file) {
                bulkUpload.uploadFile(file);
            }
        },
        [bulkUpload],
    );

    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    return (
        <div className="flex w-full flex-col gap-6">
            <div className="flex flex-wrap items-start gap-3 lg:items-center">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => history.push(backHref)}
                    className="mr-auto"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                    Back to POs
                </Button>
                <div className="flex flex-1 flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        Bulk Create Purchase Orders
                    </h1>
                </div>
            </div>
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardDescription>
                            Upload a CSV file to create multiple purchase orders at once. The file will be validated
                            before you can save.
                            <br />
                            There is currently a limit of 100 rows per bulk upload.
                            <br />
                            <b>The following actions are supported</b>
                            <br />
                            <b>add</b> - Add a new purchase order
                            <br />
                            <b>add_item</b> - Add items to an existing purchase order
                            <br />
                            <b>override</b> - Override an existing purchase order item
                            <br />
                            <b>remove</b> - Remove an existing purchase order
                            <br />
                            <b>
                                Multiple lines can be grouped as one Purchase Order by keeping the following fields the
                                same:
                                <br />
                                Facility, Vendor, Shipper, Delivery Type, Scheduled Delivery Date
                            </b>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Show dropzone if no data yet and not in success state */}
                        {!hasData && !persistSuccess && (
                            <>
                                <div className="flex justify-end">
                                    <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                        <Download className="mr-2 h-4 w-4" />
                                        Download Template
                                    </Button>
                                </div>
                                <BulkUploadDropzone
                                    onFileSelect={handleFileSelect}
                                    disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                        bulkUpload.status,
                                    )}
                                    isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                                />
                            </>
                        )}

                        <BulkUploadStatus
                            entityName="Purchase Order"
                            fetchError={bulkUpload.fetchError}
                            handleStartNew={handleStartNew}
                            hasData={hasData}
                            persistError={bulkUpload.persistError}
                            persistErrors={bulkUpload.persistErrors}
                            persistSuccess={persistSuccess}
                            status={bulkUpload.status}
                            uploadError={bulkUpload.uploadError}
                        />

                        {hasData &&
                            !persistSuccess &&
                            !bulkUpload.hasPersistErrors &&
                            ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(
                                bulkUpload.status,
                            ) && (
                                <>
                                    {/* Validation summary */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            {errorCount > 0 ? (
                                                <Alert variant="destructive" className="inline-flex">
                                                    {errorCount} validation error{errorCount !== 1 ? 's' : ''} found.
                                                    Please fix the errors before saving. You can hover over the
                                                    highlighted cells for more information.
                                                </Alert>
                                            ) : (
                                                <Alert className="inline-flex border-green-500 text-green-700">
                                                    No validation errors. Ready to save.
                                                </Alert>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            {bulkUpload.hasUnsavedChanges && (
                                                <>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={bulkUpload.clearPendingChanges}
                                                    >
                                                        Discard Changes
                                                    </Button>
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={bulkUpload.submitChanges}
                                                    >
                                                        Submit Changes
                                                    </Button>
                                                </>
                                            )}
                                            <Button variant="outline" size="sm" onClick={bulkUpload.reset}>
                                                Start Over
                                            </Button>
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={handlePersist}
                                                disabled={!bulkUpload.canPersist}
                                            >
                                                Save Purchase Orders
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Data table */}
                                    <BulkUploadTable
                                        columnDefs={PO_COLUMN_DEFS}
                                        rows={bulkUpload.rows}
                                        errorsByCell={bulkUpload.errorsByCell}
                                        pendingEdits={bulkUpload.pendingEdits}
                                        updateCell={bulkUpload.updateCell}
                                        pendingDeletes={bulkUpload.pendingDeletes}
                                        toggleRowDelete={bulkUpload.toggleRowDelete}
                                    />

                                    {/* Row count and pending changes summary */}
                                    <BulkUploadPendingChanges
                                        pendingDeletes={bulkUpload.pendingDeletes}
                                        pendingEdits={bulkUpload.pendingEdits}
                                        rowsLength={bulkUpload.rows.length}
                                    />
                                </>
                            )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export { BulkCreatePurchaseOrder };
